import type { TrainingProjection, ThisWeekTraining } from '../types/api'
import { formatDateRange as formatDateRangeUk } from './dateTranslations'

// Type for translation object - using Record to avoid circular dependencies
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationObject = any

/**
 * Calculate statistics for a set of trainings
 */
export function calculateTrainingStats(trainings: TrainingProjection[]) {
  const now = new Date()
  
  const totalTrainingsToConsider = trainings.filter(t => {
    return t.status === 'ACTIVE' || t.isFired === true
  })

  const totalCount = totalTrainingsToConsider.length
  const firedCount = trainings.filter(t => t.isFired === true).length
  const passedCount = totalTrainingsToConsider.filter(t => new Date(t.end) < now).length
  const leftCount = totalTrainingsToConsider.filter(t => new Date(t.end) >= now).length
  
  return { totalCount, firedCount, passedCount, leftCount }
}

/**
 * Find the next upcoming training from a list
 */
export function getNextTraining(trainings: TrainingProjection[]): TrainingProjection | null {
  const now = new Date()
  
  // Filter trainings that haven't ended yet
  const upcomingTrainings = trainings.filter(t => new Date(t.end) >= now)
  
  if (upcomingTrainings.length === 0) return null
  
  // Find the training with the nearest start date
  const nextTraining = upcomingTrainings.reduce((nearest, current) => {
    const nearestStart = new Date(nearest.start)
    const currentStart = new Date(current.start)
    return currentStart < nearestStart ? current : nearest
  })
  
  return nextTraining
}

/**
 * Check if a training set can be completed based on the last training's end date
 */
export function canCompleteTrainingSet(
  trainings: TrainingProjection[], 
  setStatus: string | null
): boolean {
  if (!trainings.length || !setStatus || setStatus !== 'ACTIVE') return false
  
  // Get the last training
  const lastTraining = trainings[trainings.length - 1]
  const lastTrainingEnd = new Date(lastTraining.end)
  const now = new Date()
  
  // Can complete if the last training's end date has passed
  return lastTrainingEnd <= now
}

/**
 * Group trainings by week (Monday to Sunday)
 * Returns an object where keys are week numbers and values are arrays of trainings
 */
export function groupTrainingsByWeek(trainings: TrainingProjection[]): Record<number, TrainingProjection[]> {
  if (trainings.length === 0) return {}

  return trainings.reduce((acc, training) => {
    const trainingDate = new Date(training.start)
    
    // Get the Monday of the week for this training
    const dayOfWeek = trainingDate.getDay()
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek // Adjust when day is Sunday (0) or other days
    const weekStart = new Date(trainingDate)
    weekStart.setDate(trainingDate.getDate() + diff)
    weekStart.setHours(0, 0, 0, 0)
    
    // Calculate week number based on the first training's week
    const firstTraining = trainings[0]
    const firstTrainingDate = new Date(firstTraining.start)
    const firstDayOfWeek = firstTrainingDate.getDay()
    const firstDiff = firstDayOfWeek === 0 ? -6 : 1 - firstDayOfWeek
    const firstWeekStart = new Date(firstTrainingDate)
    firstWeekStart.setDate(firstTrainingDate.getDate() + firstDiff)
    firstWeekStart.setHours(0, 0, 0, 0)
    
    // Calculate the week number (weeks since first training)
    const daysDifference = Math.floor((weekStart.getTime() - firstWeekStart.getTime()) / (1000 * 60 * 60 * 24))
    const weekNumber = Math.floor(daysDifference / 7) + 1
    
    if (!acc[weekNumber]) {
      acc[weekNumber] = []
    }
    acc[weekNumber].push(training)
    
    return acc
  }, {} as Record<number, TrainingProjection[]>)
}

/**
 * Format a date range for display
 */
export function formatDateRange(startDate: Date, endDate: Date, t: TranslationObject): string {
  return formatDateRangeUk(startDate, endDate, t)
}

export function checkOverlap(a: ThisWeekTraining, b: ThisWeekTraining): boolean {
  return new Date(a.start) < new Date(b.end) && new Date(b.start) < new Date(a.end)
}

export function findOverlappingGroups(sessions: ThisWeekTraining[]): ThisWeekTraining[][] {
  const groups: ThisWeekTraining[][] = []
  const processed = new Set<number>()
  sessions.forEach((session, index) => {
    if (processed.has(index)) return
    const group = [session]
    processed.add(index)
    for (let i = index + 1; i < sessions.length; i++) {
      if (!processed.has(i) && group.some(t => checkOverlap(t, sessions[i]))) {
        group.push(sessions[i])
        processed.add(i)
      }
    }
    groups.push(group)
  })
  return groups
}

export function formatLocalDateTimeForAPI(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
         `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

export function formatTimeRange(start: string, end: string): string {
  const fmt = (s: string) => {
    const d = new Date(s)
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }
  return `${fmt(start)} – ${fmt(end)}`
}

export function formatTraineeDisplayName(t: ThisWeekTraining): string {
  if (t.otherUserFirstName?.trim()) {
    return `${t.otherUserFirstName} ${t.otherUserLastName || ''}`.trim()
  }
  return t.otherUserUsername?.trim() || 'Unknown'
}

/**
 * Get the date range for a week number based on the first training
 */
export function getWeekDateRange(weekNumber: number, firstTraining: TrainingProjection): { start: Date; end: Date } {
  const firstTrainingDate = new Date(firstTraining.start)
  const firstDayOfWeek = firstTrainingDate.getDay()
  const firstDiff = firstDayOfWeek === 0 ? -6 : 1 - firstDayOfWeek
  const firstWeekStart = new Date(firstTrainingDate)
  firstWeekStart.setDate(firstTrainingDate.getDate() + firstDiff)
  firstWeekStart.setHours(0, 0, 0, 0)
  
  const weekStart = new Date(firstWeekStart)
  weekStart.setDate(firstWeekStart.getDate() + (weekNumber - 1) * 7)
  
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekStart.getDate() + 6)
  
  return { start: weekStart, end: weekEnd }
}
